// client/src/components/layout/MainLayout.tsx
import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, Link, useLocation, Outlet } from 'react-router-dom';
import { logout } from '../../store/authSlice';
import { RootState } from '../../store';
import { authApi } from '../../services/api';
import { useAuth } from '../../utils/auth';
import {
    Home,
    FileText,
    Box,
    Users,
    Settings,
    LogOut,
    Menu,
    X,
    UserCircle,
    RefreshCw,
    Briefcase,
    Wrench, // Use Wrench as a replacement for Tool
    Database,
} from 'lucide-react';
import DarkModeToggle from '../DarkModeToggle';
import NotificationCenter from '../NotificationCenter';
import NotificationPermissionPrompt from '../NotificationPermissionPrompt';

export default function MainLayout() {
    const { user } = useSelector((state: RootState) => state.auth);
    const { isAdmin } = useAuth();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [isUpdating, setIsUpdating] = useState(false);

    // Debug logging for user data
    // console.log('MainLayout - User Data:', {
    //     user,
    //     isAdmin,
    //     position: user?.position,
    //     department: user?.position?.department,
    //     departmentName: user?.position?.department?.name
    // });

    const hasEquipmentAccess = Boolean(isAdmin || user?.position?.department?.name === 'IT Department');

    // New permission check for Equipment Maintenance
    const hasEquipmentMaintenanceAccess = Boolean(
        isAdmin || 
        (user?.position?.department?.name && 
         user.position.department.name.toLowerCase().includes('maintenance'))
    );

    // Debug logging for access check
    // console.log('MainLayout - Equipment Access:', {
    //     hasEquipmentAccess,
    //     isAdmin,
    //     isDepartmentIT: user?.position?.department?.name === 'IT Department'
    // });

    // Handle sidebar for different screen sizes
    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth >= 768) {
                setIsSidebarOpen(true);
            } else {
                setIsSidebarOpen(false);
            }
        };

        handleResize(); // Initial check
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Close sidebar on mobile when route changes
    useEffect(() => {
        if (window.innerWidth < 768) {
            setIsSidebarOpen(false);
        }
    }, [location]);

    const handleLogout = async () => {
        try {
            await authApi.logout();
            dispatch(logout());
            navigate('/login');
        } catch (error) {
            console.error('Logout failed:', error);
        }
    };

    const handleUpdate = async () => {
        setIsUpdating(true);
        try {
            if ('serviceWorker' in navigator) {
                const registration = await navigator.serviceWorker.ready;
                await registration.update();
                // Force reload after update
                window.location.reload();
            }
        } catch (error) {
            console.error('Failed to update service worker:', error);
        } finally {
            setIsUpdating(false);
        }
    };

    const navItems = [
        { icon: Home, label: 'Dashboard', path: '/dashboard' },
        { icon: FileText, label: 'Job Cards', path: '/jobcards' },
        // { icon: Package, label: 'Inventory', path: '/inventory' },
        ...(hasEquipmentAccess ? [{ icon: Box, label: 'Equipment', path: '/equipment' }] : []),
        // Admin-specific general links
        ...(isAdmin ? [
            { icon: Users, label: 'Users', path: '/users' },
            { icon: Box, label: 'Stores', path: '/stores' },
            { icon: Briefcase, label: 'Positions', path: '/positions-departments' },
        ] : []),
        // Equipment Maintenance (conditionally shown)
        ...(hasEquipmentMaintenanceAccess ? 
            [{ icon: Wrench, label: 'Equipment Maintenance', path: '/equipment-maintenance' }]
            : []
        ),
        // Cache Management available to all users
        { icon: Database, label: 'Cache', path: '/cache-management' },
        // Admin-only Settings
        ...(isAdmin ? [
            { icon: Settings, label: 'Settings', path: '/settings' },
        ] : [])
    ];

    const isActivePath = (path: string) => location.pathname === path;

    return (
        <div className="min-h-screen bg-gray-100 dark:bg-gray-900 overflow-x-hidden">
            {/* Top Navigation Bar */}
            <nav className="fixed top-0 left-0 right-0 bg-white dark:bg-gray-800 shadow-sm z-20">
                <div className="h-16 flex items-center justify-between px-4">
                    <div className="flex items-center gap-4">
                        <button
                            onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 md:hidden"
                        >
                            <Menu size={24} className="text-gray-600 dark:text-gray-300" />
                        </button>
                        <span className="text-md font-semibold text-gray-900 dark:text-white">
                            {import.meta.env.VITE_APP_NAME}
                        </span>
                    </div>
                    <div className="flex items-center gap-4">
                        <DarkModeToggle />
                        <NotificationCenter />
                        <span className="text-sm text-gray-600 dark:text-gray-300">
                            {user?.name}
                            {isAdmin && <span className="ml-2 text-xs text-indigo-600 dark:text-indigo-400">(Admin)</span>}
                        </span>
                        <button
                            onClick={handleUpdate}
                            className={`p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-all ${isUpdating ? 'animate-spin text-indigo-600' : 'text-gray-600 dark:text-gray-300'}`}
                            title="Update application"
                            disabled={isUpdating}
                        >
                            <RefreshCw size={20} />
                        </button>
                    </div>
                </div>
            </nav>

            {/* Sidebar Overlay */}
            {isSidebarOpen && (
                <div
                    className="fixed inset-0 bg-black/50 z-30 md:hidden"
                    onClick={() => setIsSidebarOpen(false)}
                />
            )}

            {/* Sidebar */}
            <aside
                className={`fixed top-0 left-0 h-full w-64 bg-white dark:bg-gray-800 shadow-lg z-40 transform transition-transform duration-300 ease-in-out ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'
                    } md:translate-x-0 md:z-10`}
            >
                <div className="h-16 flex items-center justify-between px-4 border-b border-gray-200 dark:border-gray-700">
                    <span className="text-xl font-bold text-gray-900 dark:text-white">
                        {import.meta.env.VITE_APP_NAME}
                    </span>
                    <button
                        onClick={() => setIsSidebarOpen(false)}
                        className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 md:hidden"
                    >
                        <X size={24} className="text-gray-600 dark:text-gray-300" />
                    </button>
                </div>

                <div className="flex flex-col h-[calc(100%-4rem)]">
                    <div className="flex-1 overflow-y-auto py-4">
                        <nav className="space-y-1 px-2">
                            {navItems.map((item) => (
                                <Link
                                    key={item.label}
                                    to={item.path}
                                    className={`flex items-center px-3 py-2 rounded-lg transition-colors ${isActivePath(item.path)
                                            ? 'bg-indigo-50 dark:bg-indigo-900/50 text-indigo-600 dark:text-indigo-400'
                                            : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                                        }`}
                                >
                                    <item.icon className="w-5 h-5 mr-3" />
                                    <span>{item.label}</span>
                                </Link>
                            ))}
                        </nav>
                    </div>

                    <div className="p-4 border-t border-gray-200 dark:border-gray-700 mt-auto">
                        <Link
                            to="/profile"
                            className={`flex items-center w-full px-3 py-2 mb-2 rounded-lg transition-colors ${isActivePath('/profile')
                                    ? 'bg-indigo-50 dark:bg-indigo-900/50 text-indigo-600 dark:text-indigo-400'
                                    : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                                }`}
                        >
                            <UserCircle className="w-5 h-5 mr-3" />
                            <span>Profile</span>
                        </Link>
                        <button
                            onClick={handleLogout}
                            className="flex items-center w-full px-3 py-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/50 rounded-lg transition-colors"
                        >
                            <LogOut className="w-5 h-5 mr-3" />
                            <span>Logout</span>
                        </button>
                    </div>
                </div>
            </aside>

            {/* Main Content */}
            <main className={`transition-all duration-300 mt-16 ${isSidebarOpen ? 'md:ml-64' : ''
                }`}>
                <div className="p-4 md:p-8">
                    <NotificationPermissionPrompt />
                    <Outlet />
                </div>
            </main>
        </div>
    );
}